import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { UserRole } from '@examdojo/models/user';

export interface RoleBadgeConfig {
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

@Component({
  selector: 'exam-cms-role-badge',
  template: `
    <span [class]="badgeClasses()">
      @if (config().showIcon) {
        <svg [class]="iconClasses()" fill="currentColor" viewBox="0 0 20 20">
          @switch (role()) {
            @case ('ADMIN') {
              <!-- Shield icon for admin -->
              <path fill-rule="evenodd" d="M10 1L3 5v6c0 5.55 3.84 11 7 11s7-5.45 7-11V5l-7-4z" clip-rule="evenodd" />
            }
            @case ('CONTRIBUTOR') {
              <!-- User icon for contributor -->
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
            }
            @default {
              <!-- Default user icon -->
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
            }
          }
        </svg>
      }
      {{ role() }}
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class RoleBadgeComponent {
  readonly role = input.required<UserRole>();
  readonly config = input<RoleBadgeConfig>({});

  readonly badgeClasses = computed(() => {
    const role = this.role();
    const config = this.config();
    const size = config.size || 'md';
    const showIcon = config.showIcon || false;

    const baseClasses = 'inline-flex items-center justify-center rounded-full font-medium';
    const sizeClasses = this.getSizeClasses(size);
    const colorClasses = this.getColorClasses(role);
    const gapClass = showIcon ? 'gap-1.5' : '';
    // Fixed height for consistent alignment with program badges
    const heightClass = size === 'sm' ? 'h-5' : size === 'lg' ? 'h-7' : 'h-6';

    return `${baseClasses} ${sizeClasses} ${colorClasses} ${gapClass} ${heightClass}`.trim();
  });

  readonly iconClasses = computed(() => {
    const config = this.config();
    const size = config.size || 'md';
    return size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';
  });

  private getSizeClasses(size: 'sm' | 'md' | 'lg'): string {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      case 'lg':
        return 'px-3 py-1.5 text-sm';
      default:
        return 'px-2 py-1 text-xs';
    }
  }

  private getColorClasses(role: UserRole): string {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      case 'CONTRIBUTOR':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900/20 dark:text-sky-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  }
}
